import clsx from "clsx";
import { marked } from "marked";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import FormField from "@components/Form/FormFields/FormFields";
import FormErrorMessage from "@components/FormErrorMessage/FormErrorMessage";
import Grid from "@components/Grid/Grid";
import RichTextDisplay from "@components/RichTextDisplay/RichTextDisplay";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import setGADataLayer from "@utilities/setGADataLayer";
import { TypeRequestToJoinMyLbsFormFields } from "types/contentful/TypeRequestToJoinMyLbsForm";

import { PROGRAMME_OF_STUDY_OPTIONS, generateYearOptions } from "./constants";
import styles from "./RequestToJoinMyLbsForm.module.scss";

export const RequestToJoinMyLbsForm = (props: {
  content: TypeRequestToJoinMyLbsFormFields;
}) => {
  const { content } = props;
  const {
    formTitleText,
    formDescriptionRichText,
    lastNameLabelText,
    lastNameHintText,
    dateOfBirthLabelText,
    programmeOfStudyLabelText,
    programmeOfStudyHintText,
    programmeYearLabelText,
    programmeYearHintText,
    submitButtonText,
    failedMessage,
    successPageUrl,
    footerText,
  } = content;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const firstInteractRef = useRef(0);

  const router = useRouter();

  const { register, handleSubmit, formState, watch } = useForm({
    mode: "onBlur",
    criteriaMode: "all",
  });

  const FormFields = [
    {
      propertyName: "lastName",
      validation: {
        isRequired: true,
        maxLength: 30,
        lastName: true,
      },
      labelText: lastNameLabelText,
      hintText: lastNameHintText,
    },
    {
      propertyName: "dateOfBirth",
      validation: {
        isRequired: true,
      },
      labelText: dateOfBirthLabelText,
      type: "date",
    },
    {
      propertyName: "programmeOfStudy",
      validation: {
        isRequired: true,
      },
      labelText: programmeOfStudyLabelText,
      options: PROGRAMME_OF_STUDY_OPTIONS,
      formType: "select",
      dataText: PROGRAMME_OF_STUDY_OPTIONS[0]?.value,
      hintText: programmeOfStudyHintText,
      isEmptyFirstOption: false,
      firstOptionTextForSelectField: "Please select a programme of study",
    },
    {
      propertyName: "programmeYear",
      validation: {
        isRequired: true,
      },
      labelText: programmeYearLabelText || "Programme year",
      options: generateYearOptions(),
      formType: "select",
      hintText: programmeYearHintText,
      isEmptyFirstOption: false,
      firstOptionTextForSelectField: "Please select a programme year",
    },
  ];

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteractRef.current < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "requestToJoinMyLbsForm",
        });
        firstInteractRef.current++;
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const redirectToSuccessPage = () => {
    setGADataLayer({
      event: "formComplete",
      formName: "requestToJoinMyLbsForm",
    });

    if (successPageUrl) {
      const fullSuccessPageUrl = "/" + successPageUrl;
      router.push(fullSuccessPageUrl);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError(false);

    try {
      const response = await fetch("/api/postRequestToJoin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        redirectToSuccessPage();
        return;
      }

      // Show error for API failures
      window.scrollTo(0, 0);
      setError(true);
    } catch (_e) {
      window.scrollTo(0, 0);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wrapper component-wrap">
      <Grid row>
        <Grid column sm={12} md={6}>
          <section
            className={clsx(
              styles.component,
              styles["request-to-join-form"],
              "form",
              styles.cf
            )}
          >
            <h1 className="h2">{formTitleText || "Request to Join MyLBS"}</h1>

            {formDescriptionRichText && (
              <RichTextDisplay
                richText={formDescriptionRichText}
                tinyMceRichText={null}
              />
            )}

            <form method="POST"
              className="form"
              onSubmit={handleSubmit(onSubmit)}
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              data-netlify-recaptcha="true"
            >
              {/* Honeypot field - hidden from users, catches bots */}
              <p className={styles["honeypot-field"]}>
                <label>
                  Do not fill this out if you are human:{" "}
                  <input type="text" {...register("bot-field")} />
                </label>
              </p>

              <div className={styles["fields-floated"]}>
                <div className="form-row">
                  <section className={styles["form-group-wrapper"]}>
                    {FormFields.map((field: any, index) => {
                      return (
                        <React.Fragment key={`${field.labelText}${index}`}>
                          <FormField
                            formType={field?.formType}
                            watch={watch}
                            validation={field?.validation}
                            type={field.type || "text"}
                            register={register}
                            property={field.dataText}
                            placeholder={field.labelText}
                            options={field.options}
                            name={field?.propertyName}
                            errors={formState.errors}
                            showAllErrors={true}
                            isEmptyFirstOption={field.isEmptyFirstOption}
                            firstOptionTextForSelectField={
                              field.firstOptionTextForSelectField
                            }
                          />
                          {field?.hintText && (
                            <div
                              id={`${field.propertyName}-hint`}
                              className={styles["hint-text"]}
                              dangerouslySetInnerHTML={{
                                __html: marked.parse(field.hintText) as string,
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </section>
                </div>

                {footerText && (
                  <div className={styles["footer-text"]}>
                    <RichTextDisplay
                      richText={footerText}
                      tinyMceRichText={null}
                    />
                  </div>
                )}

                {/* Netlify reCAPTCHA */}
                <div data-netlify-recaptcha="true"></div>

                {error && (
                  <FormErrorMessage
                    failedRichText={failedMessage?.fields?.content}
                    failedTinyMceRichText={
                      failedMessage?.fields?.contentTinyMce
                    }
                  />
                )}

                <div className="btn-and-text-wrapper">
                  <SubmitButton
                    loading={loading}
                    id="submitButton"
                    text={submitButtonText || "Submit"}
                  />
                </div>
              </div>
            </form>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default RequestToJoinMyLbsForm;
